import React, { useContext } from "react";
import { Link, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { useGetSale } from "../../hooks/useSale";
import { useGetInventory } from "../../hooks/useInventory";
import { useGetLists } from "../../hooks/useList";
import Landing from "./Landing";
import Inventory from "./Inventory";
import InventoryAdd from "./InventoryAdd";
import InventoryPrint from "./InventoryPrint";
import Profile from "./Profile";
import { AuthContext, EntitiesContext, UserContext } from "../../App";
import Loader from "../Loader";
import Dates from "./Dates";
import Faqs from "./Faqs";
import UserRecord from "../../records/user";
import DashboardHeader from "./DashboardHeader";

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const user = useContext(UserContext);
  const entities = useContext(EntitiesContext);
  const sale = entities.state.get("sale").find((s) => s.open);
  const items = sale
    ? entities.state.get("inventory").filter((i) => i.sale_id === sale.id)
    : entities.state.get("inventory").filter(() => false);

  const { isLoading } = useGetSale();
  const lists = useGetLists();
  const inventory = useGetInventory();

  const tabs = [
    ["/dashboard", "Dashboard"],
    ["/dashboard/inventory", "Current Inventory"],
    ["/dashboard/inventory/add", "Add Inventory"],
    ["/dashboard/profile", "Profile"],
    ["/dashboard/dates", "Important Dates"],
    ["/dashboard/faqs", "FAQs"],
  ];

  const isLoginAsSession = window.localStorage.getItem("loginAsSession") === "true";

  const logout = () => {
    ["auth", "loginAsSession", "originalAdminAuth", "orderId"].forEach((key) =>
      window.localStorage.removeItem(key)
    );
    auth.setAuth(null);
    user.setUser(new UserRecord());
    navigate("/");
  };

  const returnToAdminLogin = () => {
    // Restore the original admin's auth
    const originalAuth = window.localStorage.getItem("originalAdminAuth");
    if (originalAuth) {
      const authData = JSON.parse(originalAuth);
      window.localStorage.setItem("auth", originalAuth);
      window.localStorage.removeItem("originalAdminAuth");
      window.localStorage.removeItem("loginAsSession");

      // Restore auth state
      auth.setAuth(authData.data.data);
      user.setUser(new UserRecord({ ...authData.data.data.attributes, is_logged_in: true }));

      navigate("/admin/cashiers");
    } else {
      // Fallback if original auth wasn't stored
      window.localStorage.removeItem("auth");
      window.localStorage.removeItem("loginAsSession");
      auth.setAuth(null);
      navigate("/");
    }
  };

  if (isLoading || lists.isLoading || inventory.isLoading) return <Loader />;

  if (!sale) {
    return (
      <div className="container">
        <DashboardHeader onSignOut={logout} />

        <div className="notification is-info">
          <div className="content has-text-centered">
            <h3>No Active Sale</h3>
            <p>
              There is no active sale at this time. Please check back later or
              contact the PTA for more information about upcoming sales.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {isLoginAsSession && (
        <div className="notification is-warning" style={{ marginBottom: "1rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>
              <strong>Logged in as:</strong> {user.user.first_name} {user.user.last_name}
            </span>
            <button className="button is-small is-link" onClick={returnToAdminLogin}>
              Return to Admin Login
            </button>
          </div>
        </div>
      )}

      <DashboardHeader title="Waitsfield Ski & Skate Sale 2026" onSignOut={logout} />

      <nav className="tabs is-centered" aria-label="Consignor sections">
        <ul>
          {tabs.map(([to, label]) => {
            const active = location.pathname === to;
            return (
              <li
                key={to}
                className={classNames({ "is-active": active })}
                aria-current={active ? "page" : undefined}
              >
                <Link to={to}>{label}</Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <Routes>
        <Route index element={<Landing items={items} />} />
        <Route path="/inventory" element={<Inventory sale={sale} items={items} />} />
        <Route path="/inventory/add" element={<InventoryAdd sale={sale} />} />
        <Route path="/inventory/:id" element={<InventoryAdd sale={sale} />} />
        <Route path="/inventory/print" element={<InventoryPrint />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/dates" element={<Dates sale={sale} />} />
        <Route path="/faqs" element={<Faqs />} />
      </Routes>
    </div>
  );
}
